import {
  Alert,
  ButtonIcon,
  ConfirmationDialog,
} from '@statsbomb/kitbag-components';
import { useAtom, useSetAtom } from 'jotai';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { metadataIsUpdatingAtom } from '../../../atoms/approval';
import { messageAwaitingApprovalActionsAtom } from '../../../atoms/messages';
import { selectedMetadataAtom } from '../../../atoms/selectedMetadata';
import useApi, { API_ENDPOINTS } from '../../../hooks/api';
import MetadataSelections from '../../MetadataSelections';

export function EditMetadata({
  matchId: initialMatchId,
  matchPart: initialMatchPart,
  sport: initialSport,
  track: initialTrack,
  version: initialVersion,
  size,
  videoKey,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const intl = useIntl();
  const api = useApi();

  const [selectedMetadata, setSelectedMetadata] = useAtom(selectedMetadataAtom);
  const {
    sport,
    matchId,
    period: matchPart,
    version,
    track,
  } = selectedMetadata;

  const setMetadataIsUpdating = useSetAtom(metadataIsUpdatingAtom);
  const setActionMessage = useSetAtom(messageAwaitingApprovalActionsAtom);

  const FAILURE_MESSAGE = intl.formatMessage(
    {
      defaultMessage: 'Metadata Update for {videoKey} failed',
      description: 'Metadata Update Failed',
      id: 'f70a7',
    },
    { videoKey },
  );
  const SUCCESS_MESSAGE = intl.formatMessage(
    {
      defaultMessage: 'Metadata Update for {videoKey} requested',
      description: 'Metadata Update Requested',
      id: 'd5c00',
    },
    { videoKey },
  );

  const formSubmit = async () => {
    setMetadataIsUpdating(true);
    const response = await api.patch(
      `${API_ENDPOINTS.videoService}/videos/${videoKey}`,
      {
        data: JSON.stringify({
          match_id: matchId,
          part: matchPart,
          sport,
          track,
          version,
        }),
      },
    );
    setActionMessage(response ? SUCCESS_MESSAGE : FAILURE_MESSAGE);
    setMetadataIsUpdating(false);
    setIsOpen(false);
  };

  const handleOpenClick = () => {
    setSelectedMetadata({
      matchId: initialMatchId,
      period: initialMatchPart,
      sport: initialSport,
      track: initialTrack,
      version: initialVersion,
    });
    setIsOpen(true);
  };

  return (
    <>
      <ButtonIcon
        icon="Edit"
        title={intl.formatMessage(
          {
            defaultMessage: 'Edit {videoKey} Metadata',
            description: 'Edit Video Metadata',
            id: '59d14',
          },
          { videoKey },
        )}
        onClick={handleOpenClick}
        size={size}
        variant="secondary"
      />
      <ConfirmationDialog
        confirmLabel={intl.formatMessage({
          defaultMessage: 'Submit',
          id: 'a4d3b',
        })}
        id="edit-metadata-form-{}"
        isOpen={isOpen}
        onCancel={() => setIsOpen(false)}
        onConfirm={formSubmit}
        title={
          <FormattedMessage
            description="Edit Metadata text"
            defaultMessage="Edit Metadata for {key}"
            id="6c67e"
            values={{ key: videoKey }}
          />
        }
      >
        {isOpen && (
          <form onSubmit={formSubmit}>
            {api.errorMessage && <Alert type="error">{api.errorMessage}</Alert>}
            <div>
              <div id="dialog-update-metadata" data-testid="metadata-selection">
                <div>
                  <MetadataSelections.Sport />
                </div>
                <div>
                  <MetadataSelections.MatchId id="matchId" required />
                </div>
                <div>
                  <MetadataSelections.MatchPart />
                </div>
                <div>
                  <MetadataSelections.Track />
                </div>
                <div>
                  <MetadataSelections.Version />
                </div>
              </div>
            </div>
          </form>
        )}
      </ConfirmationDialog>
    </>
  );
}

EditMetadata.propTypes = {
  matchId: PropTypes.string.isRequired,
  matchPart: PropTypes.string.isRequired,
  size: PropTypes.string,
  sport: PropTypes.string.isRequired,
  track: PropTypes.string.isRequired,
  version: PropTypes.string.isRequired,
  videoKey: PropTypes.string.isRequired,
};

EditMetadata.defaultProps = {
  size: undefined,
};

export default { EditMetadata };
